/*
 *  TTTech nerve-management-system-frontend
 *  Copyright(c) 2026. TTTech Industrial Automation AG.
 *
 *  ALL RIGHTS RESERVED.
 *
 *  Usage of this software, including source code, netlists, documentation,
 *  is subject to restrictions and conditions of the applicable license
 *  agreement with TTTech Industrial Automation AG or its affiliates.
 *
 *  All trademarks used are the property of their respective owners.
 *
 *  TTTech Industrial Automation AG and its affiliates do not assume any liability
 *  arising out of the application or use of any product described or shown
 *  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
 *  make changes, at any time, in order to improve reliability, function or
 *  design.
 *
 *  Contact Information:
 *  support@tttech-industrial.com
 *
 *  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
 *
 */
import useUtilsStore from '@/store/modules/pinia/utils/_api_request_handler';
import { defineStore } from 'pinia';
import RolesApiService from '@/services/api/roles.api-service';
import RoleModel from '@/model/roles/role.model';
import CategoryModel from '@/model/roles/category.model';

const useRolesStore = defineStore('roles', {
  state: () => ({
    role: new RoleModel(),
    roles: [],
    count: 0,
    categories: [],
    uiPermissions: [],
    apiPermissions: [],
    selectedUiPermissions: [],
    selectedApiPermissions: [],
    showConfirmationDialog: true,
  }),
  actions: {
    async fetchRoles(parameters = {}) {
      const order = parameters.sortBy && parameters.order ? { [parameters.sortBy]: parameters.order } : null;
      const params = {
        ...(parameters.itemsPerPage && { limit: parameters.itemsPerPage }),
        ...(parameters.page && { page: parameters.page }),
        ...(order && { order }),
        ...((parameters.search || (parameters.filterBy && parameters.filterBy !== 'all')) && {
          filterBy: {
            ...(parameters.search && { name: parameters.search }),
            ...(parameters.filterBy && parameters.filterBy !== 'all' && { type: parameters.filterBy }),
          },
        }),
      };
      const response = await RolesApiService.getAllRoles({ params });
      // These checks are necessary because of inconsistent response from the API
      const responseRoles = response.data ?? response;
      this.roles = responseRoles.map((role) => new RoleModel(role));
      this.count = response.count ?? this.roles.length;
    },

    async fetchRole(roleId) {
      if (roleId && roleId !== '') {
        const response = await RolesApiService.getRole(roleId);
        this.role = new RoleModel(response);
      } else {
        this.role = new RoleModel();
      }
    },

    async saveRole(roleId) {
      try {
        if (roleId) {
          await RolesApiService.updateRole(roleId, this.role.toPayload());
        } else {
          if (this.role.type === '') {
            this.role.type = 'local';
          }
          await RolesApiService.createRole(this.role.toPayload());
        }
      } catch (err) {
        throw err.response.data[0];
      }
    },

    async removeRole({ id }) {
      await RolesApiService.deleteRole(id);
      this.roles.splice(
        this.roles.findIndex((role) => role.id === id),
        1,
      );
      const utilsStore = useUtilsStore();
      utilsStore.showCustomToast({
        text: 'access.roles.list.deletedItem',
        color: 'success',
        showClose: true,
      });
    },

    async fetchCategories() {
      const { categories } = await RolesApiService.getCategories();
      this.categories = categories.map((category) => new CategoryModel(category));
    },

    async fetchPermissions() {
      const response = await RolesApiService.getPermissions({ searchText: '', categories: '' });
      this.uiPermissions = response.data.filter((permission) => permission.category.name === 'UI_PERMISSION');
      this.apiPermissions = response.data.filter((permission) => permission.category.name !== 'UI_PERMISSION');
      this.selectedUiPermissions = this.uiPermissions
        .filter((permission) => this.role.permissions.includes(permission._id))
        .map((permission) => permission._id);
      this.selectedApiPermissions = this.apiPermissions
        .filter((permission) => this.role.permissions.includes(permission._id))
        .map((permission) => permission._id);
    },

    deselectPermissionAndDependents(permissionId, processed = new Set()) {
      if (processed.has(permissionId)) return;
      processed.add(permissionId);

      this.selectedUiPermissions = this.selectedUiPermissions.filter((id) => id !== permissionId);
      this.selectedApiPermissions = this.selectedApiPermissions.filter((id) => id !== permissionId);

      const ancestors = this.uiPermissions.filter(
        (perm) =>
          this.selectedUiPermissions.includes(perm._id) &&
          perm.requires &&
          perm.requires.some((req) => req._id === permissionId),
      );
      ancestors.forEach((ancestor) => {
        const allRequiredSelected = ancestor.requires.every(
          (req) => this.selectedUiPermissions.includes(req._id) || this.selectedApiPermissions.includes(req._id),
        );
        if (!allRequiredSelected) {
          this.deselectPermissionAndDependents(ancestor._id, processed);
        }
      });

      const selectedApiPermissionsCopy = [...this.selectedApiPermissions];
      selectedApiPermissionsCopy.forEach((apiId) => {
        const isRequired = this.uiPermissions.some(
          (uiPerm) =>
            this.selectedUiPermissions.includes(uiPerm._id) &&
            uiPerm.requires &&
            uiPerm.requires.some((req) => req._id === apiId),
        );
        if (!isRequired) {
          this.selectedApiPermissions = this.selectedApiPermissions.filter((id) => id !== apiId);
        }
      });
    },

    selectPermissionAndDependencies(permissionId, processed = new Set()) {
      if (processed.has(permissionId)) return;
      processed.add(permissionId);

      const uiPerm = this.uiPermissions.find((p) => p._id === permissionId);
      const apiPerm = this.apiPermissions.find((p) => p._id === permissionId);
      const permission = uiPerm || apiPerm;
      if (!permission) return;

      if (uiPerm && !this.selectedUiPermissions.includes(permissionId)) {
        this.selectedUiPermissions.push(permissionId);
      }
      if (apiPerm && !this.selectedApiPermissions.includes(permissionId)) {
        this.selectedApiPermissions.push(permissionId);
      }

      if (permission.requires && Array.isArray(permission.requires)) {
        permission.requires.forEach((req) => {
          this.selectPermissionAndDependencies(req._id, processed);
        });
      }
    },

    setShowConfirmationDialog(state) {
      this.showConfirmationDialog = state;
    },
  },
});

export default useRolesStore;
